/**
 * monitoring-coverage-capacity — the setup journey's capacity verdict
 * (large-client readiness wave 3, H-8; STATUS DO NEXT ^dn-hp3i6d).
 *
 * Microsoft gives every tenant ONE shared monitoring allowance, and the
 * customer's own configuration monitors draw on it too. The capacity scan
 * (GET /tcm/coverage/{tenantId}/capacity) reports how much room is left for
 * Guardian's watch; this module turns that into the advisor's verdict shown on
 * the "Choose scope" step: fits / start smaller / add licenses or free up room /
 * blocked by existing monitors, with the number. Pure over its inputs (the
 * vitest seam: the component harness can't run effects), and every
 * customer-visible string is swept against FORBIDDEN_JARGON in its test: no
 * internal pipeline names and no capacity nouns, the same rule the meter in
 * monitoring-actions.ts follows.
 *
 * Fail open: no scan, or a scan whose monitor read failed, composes null and
 * the journey renders exactly as before. A clear room (nothing else running)
 * also composes null, because the existing meter already says everything.
 */

import type { TcmCapacityScan } from '@/types/tcm'

export interface CapacityVerdict {
  /** 'error' only for the blocking state (Neutral Bad State Rule); 'info' otherwise. */
  tone: 'info' | 'error'
  /** Action-first headline, six words or fewer, no em-dash. */
  headline: string
  /** One supporting paragraph with the numbers. */
  body: string
  /** True only when nothing can be watched until room is freed. */
  blocksSubmit: boolean
  /** Offer a "Check again" re-read (the customer changes things in Microsoft's portal). */
  offerRecheck: boolean
}

/** en-US grouping so the copy reads the same for every operator. */
function fmt(n: number): string {
  return Math.max(0, Math.trunc(n)).toLocaleString('en-US')
}

function plural(n: number, one: string, many: string): string {
  return `${fmt(n)} ${n === 1 ? one : many}`
}

/** True when the scan can drive the journey (fail open on anything else). */
function usable(scan: TcmCapacityScan | null | undefined): scan is TcmCapacityScan {
  return !!scan && scan.roomState !== 'unknown' && scan.monitors?.known === true
}

/**
 * The instance count the scope meter should measure against: the room the scan
 * left for Guardian's watch when it is known and positive, else the coverage
 * DTO's figure (`fallback`). A zero room keeps the fallback, because the blocked
 * verdict, not the meter, carries that state.
 */
export function composeWatchCeiling(scan: TcmCapacityScan | null | undefined, fallback: number): number {
  if (!usable(scan)) return fallback
  const ceiling = Math.trunc(scan.guardianInstanceCeiling)
  return ceiling > 0 ? ceiling : fallback
}

/**
 * Extra Microsoft Entra ID Governance licenses that would let `selected`
 * settings fit beside the other monitors, from the server's tier numbers;
 * null when no license count helps (past one watch's hard limit).
 */
export function licensesToFit(scan: TcmCapacityScan, selected: number): number | null {
  const cap = scan.capacity
  if (selected > cap.maxInstancesPerBaseline) return null
  const needed = Math.max(0, selected) * cap.cyclesPerDay + Math.max(0, scan.monitors.otherDailyEvaluations)
  const shortfall = needed - cap.dailyEvaluationBudget
  if (shortfall <= 0) return 0
  const perLicense = cap.premiumResourcesPerLicensePerDay > 0 ? cap.premiumResourcesPerLicensePerDay : 10
  return Math.ceil(shortfall / perLicense)
}

/** "2 configuration monitors your team already runs follow about 171 settings" */
function othersClause(scan: TcmCapacityScan): string {
  const m = scan.monitors
  const monitors = plural(m.otherMonitorCount, 'configuration monitor', 'configuration monitors')
  const one = m.otherMonitorCount === 1
  if (m.otherInstanceCount > 0) {
    return `${monitors} your team already runs in this tenant ${one ? 'follows' : 'follow'} about ${plural(m.otherInstanceCount, 'setting', 'settings')}`
  }
  return `${monitors} your team already runs in this tenant ${one ? 'shares' : 'share'} the same room`
}

/**
 * The verdict for the scope step. `selectedCount` is composeCapacityMeter's
 * sized total for the current selection (null until sizing archives).
 */
export function composeCapacityVerdict(
  scan: TcmCapacityScan | null | undefined,
  selectedCount: number | null,
): CapacityVerdict | null {
  if (!usable(scan)) return null

  const cap = scan.capacity
  const ceiling = Math.max(0, Math.trunc(scan.guardianInstanceCeiling))

  if (scan.roomState === 'blocked') {
    const capReached = scan.monitors.totalMonitorCount >= cap.maxMonitors && !scan.monitors.guardianMonitorLive
    if (capReached) {
      return {
        tone: 'error',
        headline: 'Remove a monitor to switch on.',
        body:
          `Your tenant already runs ${fmt(cap.maxMonitors)} configuration monitors, the most Microsoft allows. ` +
          'Remove one in the Microsoft Entra admin center, then check again.',
        blocksSubmit: true,
        offerRecheck: true,
      }
    }
    const licenses = selectedCount != null && selectedCount > 0 ? licensesToFit(scan, selectedCount) : null
    const licenseClause =
      licenses != null && licenses > 0
        ? `add ${plural(licenses, 'Microsoft Entra ID Governance license', 'Microsoft Entra ID Governance licenses')} to watch all ${fmt(selectedCount!)} settings`
        : 'add Microsoft Entra ID Governance licenses'
    return {
      tone: 'error',
      headline: 'Free up room to switch on.',
      body:
        `${othersClause(scan)}, which uses all the room Microsoft allows. ` +
        `Remove one in the Microsoft Entra admin center or ${licenseClause}, then check again.`,
      blocksSubmit: true,
      offerRecheck: true,
    }
  }

  // A clear room: nothing else draws on the allowance, so the meter says it all.
  if (scan.roomState !== 'shared') return null

  const roomLine = `${othersClause(scan)}, which leaves room for about ${plural(ceiling, 'setting', 'settings')} in Guardian's watch.`

  if (selectedCount == null) {
    return {
      tone: 'info',
      headline: 'Plan around your own monitors.',
      body: roomLine,
      blocksSubmit: false,
      offerRecheck: false,
    }
  }

  if (selectedCount <= ceiling) {
    return {
      tone: 'info',
      headline: 'Switch on with this selection.',
      body: `${roomLine} All ${plural(selectedCount, 'setting', 'settings')} you picked fit.`,
      blocksSubmit: false,
      offerRecheck: false,
    }
  }

  const licenses = licensesToFit(scan, selectedCount)
  if (licenses != null && licenses > 0) {
    return {
      tone: 'info',
      headline: 'Add licenses or free up room.',
      body:
        `${roomLine} To watch all ${fmt(selectedCount)}, add ` +
        `${plural(licenses, 'Microsoft Entra ID Governance license', 'Microsoft Entra ID Governance licenses')} ` +
        'or remove a monitor in the Microsoft Entra admin center. You can also start smaller and widen later.',
      blocksSubmit: false,
      offerRecheck: true,
    }
  }

  return {
    tone: 'info',
    headline: 'Start with a smaller selection.',
    body:
      `${roomLine} One watch follows at most about ${plural(cap.maxInstancesPerBaseline, 'setting', 'settings')}, ` +
      'so remove a workload to continue. You can widen coverage again later.',
    blocksSubmit: false,
    offerRecheck: false,
  }
}
